import { useState, useEffect, useMemo } from 'react';
import { skipToken } from '@reduxjs/toolkit/query';

import { useCooperativeId } from 'hooks';
import { CommonCase as CommonCaseType } from 'models';
import services from 'services';

import { fileLoaderApi, EntityType } from 'features/file-loader';

import { CommonCaseDetails } from 'components';

interface LocalState {
  commonCase: CommonCaseType | null;
  loading: boolean;
  error: boolean;
}

interface Props {
  id: string;
  constructorCase: boolean;
}

const CommonCase: React.FC<Props> = ({ id, constructorCase }) => {
  const coopId = useCooperativeId();
  const [state, setState] = useState<LocalState>({
    commonCase: null,
    loading: false,
    error: false,
  });

  fileLoaderApi.useGetAttachmentsQuery(
    coopId && id
      ? {
          cooperativeId: coopId,
          entityId: id,
          entityType: EntityType.CommonCase,
          cached: true,
        }
      : skipToken,
    {
      refetchOnMountOrArgChange: true,
    }
  );

  const url = useMemo(() => {
    if (!id) return null;
    return constructorCase
      ? `/api/constructor/cooperatives/${coopId}/cases/common/${id}`
      : `/api/cooperatives/${coopId}/cases/common/${id}`;
  }, [id, coopId, constructorCase]);

  useEffect(() => {
    const fetchCommonCase = async (url: string) => {
      try {
        setState((prevState) => ({
          ...prevState,
          loading: true,
          error: false,
        }));

        const commonCase: CommonCaseType = (await services.get(url)).data.Data;

        setState((prevState) => ({
          ...prevState,
          commonCase,
          loading: false,
        }));
      } catch (err) {
        console.error(err);
        setState((prevState) => ({
          ...prevState,
          error: true,
          loading: false,
        }));
      }
    };

    if (url) fetchCommonCase(url);
  }, [url]);

  return <CommonCaseDetails {...state} />;
};

export default CommonCase;
